import { useCallback, useEffect, useMemo, useState } from 'react';
import createContext from 'constate';
import { useQueryGetTableMap } from '../../hooks/useTableMapApi';
import { useTableMapAvailabilityValue } from './TableMapAvailabilityContext';

const selectedTableMapKey = 'selected-table-map';

interface Props {
  locationId: number;
  enabled?: boolean;
}

const [TableMapContextProvider, useTableMap] = createContext(({ locationId, enabled = true }: Props) => {
  const [isLoadingMaps, setIsLoadingMaps] = useState(true);

  const { zoneId, tableMapSummaries, isTableMapEnabled, isLoadingAvailability } = useTableMapAvailabilityValue({
    locationId,
  });

  const [selectedTableMapId, setSelectedTableMapId] = useState<string | undefined>();
  const { data: selectedTableMap } = useQueryGetTableMap(locationId, zoneId, selectedTableMapId, enabled);

  const [selectedTableId, setSelectedTableId] = useState<string | undefined>();
  const selectedTable = selectedTableMap?.tableMap?.tables.find((table) => table.id === selectedTableId);

  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (enabled && tableMapSummaries.length > 0) {
      const storedId = localStorage.getItem(selectedTableMapKey);
      const match = tableMapSummaries.find((map) => map.id === storedId) ?? tableMapSummaries[0];
      if (match) {
        setSelectedTableMapId(match.id);
      }
    }

    if (!isLoadingAvailability) {
      setIsLoadingMaps(false);
    }
  }, [enabled, isLoadingAvailability, tableMapSummaries]);

  const setSelectedTableMap = useCallback((tableMapId: string) => {
    setZoom(1);
    setSelectedTableId(undefined);
    setSelectedTableMapId(tableMapId);
    localStorage.setItem(selectedTableMapKey, tableMapId);
  }, []);

  const setSelectedTable = useCallback((tableId: string | undefined) => {
    setSelectedTableId(tableId);
  }, []);

  return useMemo(
    () => ({
      isLoadingMaps,
      isTableMapEnabled,
      selectedTable,
      selectedTableId,
      selectedTableMap: selectedTableMap?.tableMap,
      setSelectedTableId: setSelectedTable,
      setSelectedTableMap,
      setZoom,
      tableMapSummary: tableMapSummaries,
      zoom,
    }),
    [
      isTableMapEnabled,
      tableMapSummaries,
      isLoadingMaps,
      selectedTableMap?.tableMap,
      setSelectedTableMap,
      selectedTable,
      setSelectedTable,
      selectedTableId,
      zoom,
    ]
  );
});

export { TableMapContextProvider, useTableMap };
